import { ReactNode } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
} from "@/components/icons";

interface ReviewPagerProps {
  currentIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Per-item review flags (length should equal total). When provided,
   * the inline progress strip differentiates reviewed from pending. */
  reviewedFlags?: boolean[];
  className?: string;
}

const SEGMENT_LIMIT = 24;

/**
 * Single bottom-anchored bar combining progress, counter, and prev/next
 * controls. Lives in the dominant-hand thumb zone (bottom-right) so the
 * primary "Successivo" action is the easiest tap on a phone.
 *
 * Layout per row:
 *
 *   ▰▰▰▱▱▱▱▱▱▱        3 di 10 · ✓2        ←   →
 *   └ progress ─────┘ └─ counter ───┘  └ controls ┘
 *
 * Designed to live as the last child of a `flex flex-col` parent so the
 * bar takes its own layout space — content above can never scroll under
 * it. `safe-area-inset-bottom` keeps the bar above iOS home indicators
 * and Android gesture areas.
 *
 * Palette and focus treatments match `@/components/ui/Button` so the
 * pager feels like part of the same design system.
 */
export function ReviewPager({
  currentIndex,
  total,
  onPrev,
  onNext,
  reviewedFlags,
  className = "",
}: ReviewPagerProps) {
  if (total === 0) return null;

  const atStart = currentIndex <= 0;
  const atEnd = currentIndex >= total - 1;
  const useSegments = total > 0 && total <= SEGMENT_LIMIT;
  const reviewedCount =
    reviewedFlags?.reduce((acc, flag) => acc + (flag ? 1 : 0), 0) ?? 0;

  return (
    <nav
      aria-label="Navigazione revisione"
      className={`shrink-0 border-t border-gray-200 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/85 ${className}`}
    >
      <div
        className="mx-auto flex w-full max-w-2xl items-center gap-3 px-4 pt-2 sm:gap-4 lg:px-6"
        style={{
          // The safe-area inset (home indicator on notched iPhones) PLUS a
          // baseline gap, so the buttons always keep breathing room above the
          // bottom edge rather than sitting flush against it. Requires
          // `viewport-fit=cover` in the viewport meta for the inset to resolve.
          paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)",
        }}
      >
        {/* Progress strip — first because it's the visual anchor that
            tells you "where you are". Flex-1 lets it absorb extra width. */}
        <div className="min-w-0 flex-1">
          {useSegments ? (
            <ProgressSegments
              total={total}
              currentIndex={currentIndex}
              reviewedFlags={reviewedFlags}
            />
          ) : (
            <ProgressRail total={total} currentIndex={currentIndex} />
          )}
        </div>

        {/* Counter — small, tabular figures, hidden on the narrowest
            screens since the controls cluster also carries position info. */}
        <div className="hidden shrink-0 items-baseline gap-1 tabular-nums xs:flex sm:flex">
          <span className="text-sm font-semibold leading-none text-gray-900">
            {currentIndex + 1}
          </span>
          <span className="text-xs font-normal leading-none text-gray-500">
            di {total}
          </span>
          {reviewedFlags && reviewedCount > 0 && (
            <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-emerald-700">
              <CheckIcon size={11} className="text-emerald-600" />
              {reviewedCount}
            </span>
          )}
        </div>

        {/* Controls — paired buttons, right-aligned. Next gets the filled
            treatment because it's the primary action of a review flow. The
            gap keeps prev/next far enough apart to avoid thumb mis-taps. */}
        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          <PagerButton
            onClick={onPrev}
            disabled={atStart}
            aria-label="Precedente"
          >
            <ChevronLeftIcon size={22} />
          </PagerButton>
          <PagerButton
            onClick={onNext}
            disabled={atEnd}
            aria-label="Successivo"
            variant="primary"
          >
            <ChevronRightIcon size={22} />
          </PagerButton>
        </div>
      </div>
    </nav>
  );
}

interface PagerButtonProps {
  onClick: () => void;
  disabled: boolean;
  "aria-label": string;
  variant?: "default" | "primary";
  children: ReactNode;
}

function PagerButton({
  onClick,
  disabled,
  "aria-label": ariaLabel,
  variant = "default",
  children,
}: PagerButtonProps) {
  const base =
    "inline-flex h-12 w-12 items-center justify-center rounded-full font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40";
  const variantClass =
    variant === "primary"
      ? "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500"
      : "bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${base} ${variantClass}`}
    >
      {children}
    </button>
  );
}

interface ProgressSegmentsProps {
  total: number;
  currentIndex: number;
  reviewedFlags?: boolean[];
}

function ProgressSegments({
  total,
  currentIndex,
  reviewedFlags,
}: ProgressSegmentsProps) {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={Math.min(currentIndex + 1, total)}
      className="flex w-full items-center gap-1"
    >
      {Array.from({ length: total }).map((_, i) => {
        const isCurrent = i === currentIndex;
        const isReviewed = reviewedFlags?.[i] === true;
        const isPast = i < currentIndex;

        let segmentClass: string;
        if (isCurrent) {
          segmentClass = "bg-blue-600 h-[3px]";
        } else if (isReviewed) {
          segmentClass = "bg-emerald-500 h-[2px]";
        } else if (isPast) {
          segmentClass = "bg-gray-400 h-[2px]";
        } else {
          segmentClass = "bg-gray-200 h-[2px]";
        }

        return (
          <span
            key={i}
            className={`flex-1 rounded-full transition-[background-color,height] duration-300 ease-out ${segmentClass}`}
          />
        );
      })}
    </div>
  );
}

interface ProgressRailProps {
  total: number;
  currentIndex: number;
}

function ProgressRail({ total, currentIndex }: ProgressRailProps) {
  const percent =
    total <= 1 ? 0 : Math.min(100, (currentIndex / (total - 1)) * 100);
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={Math.min(currentIndex + 1, total)}
      className="relative h-[2px] w-full overflow-hidden rounded-full bg-gray-200"
    >
      <div
        className="absolute inset-y-0 left-0 bg-gray-400 transition-[width] duration-300 ease-out"
        style={{ width: `${percent}%` }}
      />
      <span
        aria-hidden="true"
        className="absolute top-1/2 h-[6px] w-[6px] -translate-y-1/2 rounded-full bg-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,0.18)] transition-[left] duration-300 ease-out"
        style={{ left: `calc(${percent}% - 3px)` }}
      />
    </div>
  );
}
